import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import vtkDataArray from 'vtk.js/Sources/Common/Core/DataArray';
import vtkRenderer from 'vtk.js/Sources/Rendering/Core/Renderer';
import vtkLookupTable from 'vtk.js/Sources/Common/Core/LookupTable';

import { mat4 } from 'gl-matrix';

import { vtkUtil } from './vtkUtils';
import { Mesh, OccluMesh } from '../model/Mesh';

const SCALAR_RANGE_MIN = -0.4;
const SCALAR_RANGE_MAX = 1.0;

const PurpleClr = [718 / 1000.0, 0, 718 / 1000.0, 1.0];
const RedClr = [920 / 1000.0, 80 / 1000.0, 80 / 1000.0, 1.0];
const OrangeClr = [910 / 1000.0, 530 / 1000.0, 80 / 1000.0, 1.0];
const YellowClr = [920 / 1000.0, 800 / 1000.0, 80 / 1000.0, 1.0];
const GreenClr = [740 / 1000.0, 960 / 1000.0, 80 / 1000.0, 1.0];
const BlueClr = [80 / 1000.0, 910 / 1000.0, 740 / 1000.0, 1.0];
const NormalClr = [200, 200, 200, 255];

class OccluUtil {
  static createSpreadUpTransform(upActor: vtkActor, lowActor: vtkActor) {
    const up = vtkUtil.getPolyData(upActor);
    const low = vtkUtil.getPolyData(lowActor);
    if (!up || !low) {
      return mat4.create();
    }
    const bd_up = up.getBounds();
    const bd_low = low.getBounds();
    const box_y_low = bd_low[3] - bd_low[2];
    const box_y_up = bd_up[3] - bd_up[2];
    const box_y_max = box_y_low > box_y_up ? box_y_low : box_y_up;
    const box_x_low = bd_low[1] - bd_low[0];
    const box_x_up = bd_up[1] - bd_up[0];
    const box_x_max = box_x_low > box_x_up ? box_x_low : box_x_up;
    let dist_between_archs = 0;
    if (box_y_max > 48.0) {
      // handle for full arch overlap
      dist_between_archs = box_y_max + 14;
    } else {
      dist_between_archs = box_y_max + box_y_max / 4 + box_x_max / 4;
    }
    let m = mat4.fromValues(1, 0, 0, 0, 0, -1, 0, dist_between_archs, 0, 0, -1, 0, 0, 0, 0, 1);
    mat4.transpose(m, m);
    return m;
  }

  static toOccluWorkerMeshData(actor: vtkActor) {
    const ret = new OccluWorkerMeshData();
    const polyData = vtkUtil.getPolyData(actor);
    if (!polyData) {
      return ret;
    }
    ret.verts = polyData.getPoints().getData() as Float32Array;
    ret.cells = polyData.getPolys().getData() as Uint32Array;
    return ret;
  }

  static updatePressureScalars(actor: vtkActor, values: Float64Array) {
    const polyData = vtkUtil.getPolyData(actor);
    if (!polyData || !values || !polyData.getPointData()) {
      return;
    }
    const scalars = vtkDataArray.newInstance({
      numberOfComponents: 1,
      values: values,
    });
    polyData.getPointData().setScalars(scalars);
  }

  static setCameraforSpreadMesh(render: vtkRenderer) {
    if (!render || !render.getActiveCamera()) {
      return;
    }
    const cam = render.getActiveCamera();
    cam.setFocalPoint(0, 0, 0);
    cam.setPosition(0, 0, 1);
    cam.setViewUp(0, 1, 0);
    render.resetCamera();
  }

  static createLut() {
    const NumOfColors = 8;
    const NumOfComponents = 4; //RGBA
    const table = vtkDataArray.newInstance({
      numberOfComponents: NumOfComponents,
      size: NumOfColors * NumOfComponents,
      dataType: 'Uint8Array',
    });
    table.setTuple(0, this.intColor(PurpleClr));
    table.setTuple(1, this.intColor(PurpleClr));
    table.setTuple(2, this.intColor(RedClr));
    table.setTuple(3, this.intColor(OrangeClr));
    table.setTuple(4, this.intColor(YellowClr));
    table.setTuple(5, this.intColor(GreenClr));
    table.setTuple(6, this.intColor(BlueClr));
    table.setTuple(7, NormalClr);

    const lut = vtkLookupTable.newInstance();
    lut.setTable(table);
    lut.setMappingRange(SCALAR_RANGE_MIN, SCALAR_RANGE_MAX);
    lut.build();
    return lut;
  }

  static spreadUpperandLowerArch(up: vtkActor, low: vtkActor) {
    const mat = this.createSpreadUpTransform(up, low);
    up.setUserMatrix(mat);
  }

  static setLookupTable(actor: vtkActor, lut: vtkLookupTable) {
    if (actor && actor.getMapper()) {
      actor.getMapper().setLookupTable(lut);
    }
  }

  static setMapperScalarRange(actor: vtkActor) {
    if (actor && actor.getMapper()) {
      actor.getMapper().setScalarRange(SCALAR_RANGE_MIN, SCALAR_RANGE_MAX);
    }
  }

  static createOccluMesh(mesh: Mesh) {
    if (!mesh.actor) {
      return null;
    }
    const ret = new OccluMesh(mesh.name);
    ret.actor = vtkUtil.buildActorFromDeepCopy(vtkUtil.getPolyData(mesh.actor));
    return ret;
  }

  static intColor(arr: number[]) {
    const ret: number[] = [];
    arr.forEach((e) => {
      e = Math.min(e, 1.0);
      if (e === 1.0) {
        e *= 255;
      } else {
        e *= 255.999;
        e = Math.floor(e);
      }
      ret.push(e);
    });
    return ret;
  }
}

class OccluWorkerMeshData {
  verts: Float32Array;
  cells: Uint32Array;
}

enum OccluSelectFlag {
  BOTH = 0, // compute upper and lower both
  UPPER = 1, // compute upper only
  LOWER = 2, // compute lower only
}

class OccluWorkerInMsg {
  taskId: string;
  upper: OccluWorkerMeshData;
  lower: OccluWorkerMeshData;
  selectFlag: OccluSelectFlag;
  constructor() {
    this.selectFlag = OccluSelectFlag.BOTH;
  }
}

class OccluWorkerOutMsg {
  taskId: string;
  selectFlag: OccluSelectFlag;
  upperScalars: Float64Array;
  lowerScalars: Float64Array;
}

enum WasmOccluCmd {
  InitWasm = 'cmd-init-wasm',
  InitWasmSuccess = 'cmd-init-wasm-success',
  InitWasmSuccessUpper = 'cmd-init-wasm-success-upper',
  InitWasmSuccessLower = 'cmd-init-wasm-success-lower',
  InitWasmFail = 'cmd-init-wasm-fail',
}

export { OccluUtil, OccluWorkerMeshData, OccluWorkerInMsg, OccluWorkerOutMsg, OccluSelectFlag, WasmOccluCmd };
